// Mapping functions that aims to create a naming convention for communications between
// this MapViewer and the mobile SDKs.

import { degreesToRadians, radiansToDegrees } from "@turf/helpers";

import type { Location, Poi, PoiCategories, PoiCategory } from "@/libs/models";
import type { ExternalLocation } from "../location/libs/location";
import type { Route, RouteIndication } from "../navigation/types";
import { IndicationType } from "./navigation";

export interface SDKPoi {
  buildingIdentifier: string;
  categories: SDKPoiCategory[];
  categoryIdentifier: string;
  customFields: any;
  floorIdentifier: string;
  identifier: string;
  infoHtml: string;
  poiCategoryName: string;
  poiName: string;
  position: any;
}

export interface SDKPoiCategory {
  customFields: any;
  icon_selected: string;
  icon_unselected: string;
  identifier: string;
  name: SDKMultilanguageString;
  poiCategoryCode: string;
  poiCategoryName: string;
}

export interface SDKMultilanguageString {
  en: string;
  es: string;
}

const changeField = (
  oldKey: string,
  newKey: string,
  payload: any,
  valueAsString?: boolean,
) => {
  if (oldKey in payload) {
    payload[newKey] = valueAsString ? `${payload[oldKey]}` : payload[oldKey];
    delete payload[oldKey];
  }
};

const deepCopyProperties = <T>(o: T): T => {
  return JSON.parse(JSON.stringify(o)) as T;
};

// Map messages from SDKs to this MapViewer.
// The original will be modified.

export const mapLocationFromSDK = (payload: any) => {
  changeField("buildingIdentifier", "buildingId", payload);
  changeField("floorIdentifier", "floorId", payload);

  if (payload.coordinate) {
    payload["latitude"] = payload.coordinate.latitude;
    payload["longitude"] = payload.coordinate.longitude;
  }
  if (payload.cartesianCoordinate) {
    payload["x"] = payload.cartesianCoordinate.x;
    payload["y"] = payload.cartesianCoordinate.y;
  }
  if (payload.bearing) {
    if (
      typeof payload.bearing == "object" &&
      typeof payload.bearing.radians == "number"
    ) {
      payload.bearing = 360 - radiansToDegrees(payload.bearing.radians);
    }
  }
};

export const mapPoiToSDK = (poi: Poi, poiCategories?: PoiCategories) => {
  const currentCategory = poiCategories?.findCategoryById(poi.categoryId);
  const poiCates: SDKPoiCategory[] | undefined = poiCategories?.categories.map(
    (cat) => mapPoiCateogoryToSDK(cat),
  );
  const position = {
    buildingIdentifier: String(poi.buildingId),
    cartesianCoordinate: { x: poi.position.x, y: poi.position.y },
    coordinate: { latitude: poi.position.lat, longitude: poi.position.lng },
    floorIdentifier: String(poi.position.floor_id),
  };
  const sdkPoiTo: SDKPoi = {
    buildingIdentifier: String(poi.buildingId),
    categories: poiCates || [],
    categoryIdentifier: String(poi.categoryId),
    customFields: poi.customFields.cf,
    floorIdentifier: String(poi.position.floor_id),
    identifier: String(poi.id),
    infoHtml: poi.info || "",
    poiCategoryName: currentCategory?.name || "",
    poiName: poi.name,
    position: position,
  };
  return sdkPoiTo;
};

const mapPoiCateogoryToSDK = (poiCat: PoiCategory): SDKPoiCategory => {
  const relativeIconUrl = poiCat.iconUrl?.split("/uploads")[1];
  const iconUrlParsed = relativeIconUrl ? `/uploads${relativeIconUrl}` : null;
  const multilanguageString: SDKMultilanguageString = {
    en: poiCat.getCustomFieldValue("name_en") || "",
    es: poiCat.getCustomFieldValue("name_es") || "",
  };
  const payloadReturn: SDKPoiCategory = {
    customFields: poiCat.customFields.cf,
    icon_selected: iconUrlParsed || "",
    icon_unselected: iconUrlParsed || "",
    identifier: String(poiCat.id),
    name: multilanguageString,
    poiCategoryCode: poiCat.code,
    poiCategoryName: poiCat.name,
  };
  return payloadReturn;
};

/**
 * Make sure we keep the standad indication types currently used on
 * mobile SDKs. This should be removed once there is support for the new indication
 * types on mobile.
 **/
export const mapWASMRouteToSDK = (route: Route) => {
  const mappedRoute = deepCopyProperties(route);
  if (mappedRoute?.indications) {
    mappedRoute.indications = mappedRoute?.indications.map((indication) =>
      mapWASMIndicationToSDK(indication),
    );
  }
  if (mappedRoute?.currentIndication) {
    mappedRoute.currentIndication = mapWASMIndicationToSDK(
      mappedRoute.currentIndication,
    );
  }
  if (mappedRoute?.nextIndication) {
    mappedRoute.nextIndication = mapWASMIndicationToSDK(
      mappedRoute.nextIndication,
    );
  }

  return mappedRoute;
};

const mapWASMIndicationToSDK = (indication: RouteIndication) => {
  const mappedIndication = deepCopyProperties(indication);
  if (
    mappedIndication.indicationType == IndicationType.LOOK_AT ||
    mappedIndication.indicationType == IndicationType.TURN_SLIGHT
  ) {
    mappedIndication.indicationType = IndicationType.TURN;
  }
  return mappedIndication;
};

/**
 * Maps an object of type Location to the SDK's ExternalLocation object definition
 * @param payload
 * @returns
 */
export const mapLocationToExternalLocationSDK = (
  location: Location,
): ExternalLocation => {
  const payload = deepCopyProperties(location);
  changeField("buildingId", "buildingIdentifier", payload, true);
  changeField("floorId", "floorIdentifier", payload, true);
  const hasBearing =
    payload?.hasBearing && payload?.bearingCartesian !== undefined;
  const bearingInDegrees = -payload?.bearingCartesian;
  const bearingInRadians = degreesToRadians(bearingInDegrees);
  return {
    //@ts-expect-error
    buildingIdentifier: payload.buildingIdentifier,
    coordinate: {
      latitude: payload.latitude,
      longitude: payload.longitude,
    },
    //@ts-expect-error
    floorIdentifier: payload.floorIdentifier,
    ...(hasBearing
      ? {
          bearing: {
            degrees: bearingInDegrees,
            degreesClockwise: (450 - bearingInDegrees) % 360,
            radians: bearingInRadians,
            radiansMinusPiPi:
              ((bearingInRadians + Math.PI) % (2 * Math.PI)) - Math.PI,
          },
        }
      : {}),
    accuracy: payload.accuracy,
  };
};
